<script setup>
// One upcoming-date card on the meet hub (mockups/redesign/meet.html .occ-card):
// the WHOLE card links to the event page (date + my-status chip + time + capacity
// + a chevron), so members can always open a date to RSVP, change or cancel.
// The nearest unanswered date also renders the inline RSVP row via the #rsvp slot.
import {computed} from 'vue';
import {useRouter} from 'vue-router';
import MeetCapacityBar from '~/components/meets/MeetCapacityBar.vue';
import {useMeetFormat, AttendanceStatus} from '~/composables/useMeetFormat';

const props = defineProps({
  meet: {type: Object, required: true},
  occurrence: {type: Object, required: true},
  // MeetAttendanceResponse | null | undefined (undefined = unknown / not loaded)
  myAttendance: {type: Object, default: undefined},
  // Hide personal status entirely (non-members see counts only)
  obfuscated: {type: Boolean, default: false},
});

const router = useRouter();
const {
  occurrenceDay, occurrenceTime, capacityPercent, isFull, spotsLeft, maxAttendees,
  occurrenceCancelled, attendanceChip, rsvpNotYetOpen, rsvpOpensText, rsvpOpensInText,
} = useMeetFormat();

// "1 going" / "3 of 8 going" — compact, rides next to the time chip.
const capMax = computed(() => maxAttendees(props.meet, props.occurrence));
const goingText = computed(() => capMax.value != null
    ? `${props.occurrence.attendingCount} of ${capMax.value} going`
    : `${props.occurrence.attendingCount} going`);
// What's left for the capacity-bar row (the going count lives in the chip now).
const spotsText = computed(() => {
  if (isFull(props.meet, props.occurrence)) {
    const wl = props.occurrence.waitlistedCount > 0 ? ` · waitlist of ${props.occurrence.waitlistedCount}` : '';
    return `Full${wl}`;
  }
  const left = spotsLeft(props.meet, props.occurrence);
  return `${left} spot${left === 1 ? '' : 's'} left`;
});

// The not-open gate gets a loud banner INSIDE the card — a quiet grey note
// reads as decoration; members must instantly see they can't RSVP yet and
// exactly when they can.
const notOpen = computed(() =>
    rsvpNotYetOpen(props.occurrence) && !occurrenceCancelled(props.occurrence));

const chip = computed(() => {
  if (occurrenceCancelled(props.occurrence)) {
    return {label: 'Cancelled by the club', chipClass: 'tl-chip--warn', icon: 'mdi-calendar-remove'};
  }
  if (props.obfuscated || props.myAttendance === undefined) return null;
  return attendanceChip(props.myAttendance);
});

const eventRoute = computed(() => ({
  name: 'MeetOccurrenceDetail',
  params: {meetId: props.meet.id, occurrenceId: props.occurrence.id},
}));
const go = () => router.push(eventRoute.value);

// Signpost what tapping the card does — including the opt-out — when the row
// isn't already showing the inline RSVP buttons.
const holdingSpot = computed(() => {
  const s = props.myAttendance?.status;
  return s === AttendanceStatus.Attending || s === AttendanceStatus.Waitlisted
      || s === AttendanceStatus.PromotionOffered;
});
const actionHint = computed(() => {
  if (props.obfuscated || occurrenceCancelled(props.occurrence) || notOpen.value) return 'View details';
  if (holdingSpot.value) return "Change or can't make it";
  if (props.myAttendance == null) return 'Open to RSVP';
  return 'View details';
});
</script>

<template>
  <div class="tl-card occ-card" role="link" tabindex="0" @click="go" @keydown.enter="go">
    <div class="occ-card__top">
      <span class="occ-card__title">{{ occurrenceDay(occurrence) }}</span>
      <div class="occ-card__topright">
        <span v-if="chip" class="tl-chip" :class="chip.chipClass">
          <v-icon v-if="chip.icon" size="13" :icon="chip.icon" class="mr-1"/>{{ chip.label }}
        </span>
        <v-icon size="20" icon="mdi-chevron-right" class="occ-card__chev"/>
      </div>
    </div>
    <div class="occ-card__meta">
      <span class="tl-chip"><v-icon size="13" icon="mdi-clock-outline" class="mr-1"/>{{ occurrenceTime(occurrence) }}</span>
      <span class="tl-chip"><v-icon size="13" icon="mdi-account-multiple-outline" class="mr-1"/>{{ goingText }}</span>
    </div>
    <div v-if="capMax != null" class="occ-card__cap">
      <meet-capacity-bar
          :percent="capacityPercent(meet, occurrence)"
          :full="isFull(meet, occurrence)"
          class="occ-card__bar"
      />
      <span>{{ spotsText }}</span>
    </div>

    <!-- RSVPs-not-open — impossible to miss, with the exact open moment -->
    <div v-if="notOpen" class="occ-card__notopen">
      <v-icon size="18" icon="mdi-lock-clock"/>
      <div>
        <b>RSVPs aren't open yet</b>
        <span>{{ rsvpOpensText(occurrence) }}<template v-if="rsvpOpensInText(occurrence)"> · opens {{ rsvpOpensInText(occurrence) }}</template></span>
      </div>
    </div>
    <!-- inline RSVP (nearest unanswered): stop clicks bubbling to card navigation -->
    <div v-if="$slots.rsvp" class="occ-card__rsvp" @click.stop>
      <slot name="rsvp"/>
    </div>
    <div v-else class="occ-card__hint">
      {{ actionHint }}<v-icon size="15" icon="mdi-chevron-right"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
.occ-card {
  display: block;
  padding: 14px 16px;
  cursor: pointer;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;

  & + & {
    margin-top: 10px;
  }

  &:hover {
    border-color: rgb(var(--v-theme-primary));
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 2px;
  }

  &:hover .occ-card__chev {
    color: rgb(var(--v-theme-primary));
    transform: translateX(2px);
  }

  &__top {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  &__topright {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__chev {
    color: rgb(var(--v-theme-textSubtle));
    transition: transform 0.12s ease, color 0.12s ease;
    flex-shrink: 0;
  }

  &__hint {
    display: flex;
    align-items: center;
    gap: 2px;
    margin-top: 11px;
    font-size: 12px;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.2px;
    color: inherit;
  }

  &__meta {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-top: 9px;
  }

  &__cap {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 11px;
    font-size: 11.5px;
    color: rgb(var(--v-theme-textSubtle));
    font-weight: 600;
  }

  &__bar {
    flex: 1;
    max-width: 260px;
  }

  &__rsvp {
    display: flex;
    gap: 8px;
    margin-top: 12px;
    align-items: center;
    flex-wrap: wrap;
  }

  &__notopen {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(var(--v-theme-warning), 0.12);
    color: rgb(var(--v-theme-text));

    .v-icon {
      color: rgb(var(--v-theme-warning));
      margin-top: 1px;
    }

    b {
      display: block;
      font-size: 13px;
      letter-spacing: -0.1px;
    }

    span {
      display: block;
      font-size: 12px;
      font-weight: 600;
      color: rgb(var(--v-theme-textSubtle));
      margin-top: 1px;
    }
  }
}
</style>
